import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { signInWithCustomToken, onAuthStateChanged } from "firebase/auth";

import authService from "~/services/authService";
import { verifyAuthCode } from "~/services/loginService";
import { Firebase } from "~/services/firebase";
import authAction from "~/store/actions/authAction";
import { setMe } from "~/store/actions/meAction";
import { mixpanelTrackEvent } from "~/utils/mixpanel";

// Parâmetros de URL que indicam fluxo de auth via link mágico.
// Adicione novos params aqui conforme necessário.
const AUTH_CODE_PARAMS = ["auth_code", "token"];

// auth_code enviado no link de booking quando o cliente não tem telefone (user de e-mail).
// Não há login automático: a tela pede login com Google.
export const EMAIL_LOGIN_AUTH_CODE = "LOGINEMAIL";

// Extrai o authCode do valor bruto do param.
// - "auth_code": o valor é diretamente o código
// - "token": formato "UUID.AUTHCODE" — extrai a parte após o último ponto
const parseAuthCodeFromParam = (param, value) => {
	if (!value) return null;
	if (param === "token") {
		const dotIndex = value.lastIndexOf(".");
		return dotIndex > 0 ? value.slice(dotIndex + 1) : null;
	}
	return value;
};

// Tenta extrair o businessId de padrões de URL conhecidos para o redirect de fallback.
const extractBusinessIdFromPath = (pathname) => {
	const patterns = [
		/\/business\/(?!link\/)([^/]+)/,
		/\/selecionar-data\/([^/]+)/,
		/\/bookings\/([^/]+)/,
		/\/confirmation\/([^/]+)/,
		/\/later-payment\/([^/]+)/,
	];
	for (const pattern of patterns) {
		const match = pathname.match(pattern);
		if (match) return match[1];
	}
	return null;
};

/**
 * Centraliza o fluxo de auto-login via link mágico (auth_code / token na URL).
 *
 * - Seta window.__PRIT_CODE_LOGIN_IN_PROGRESS__ de forma SÍNCRONA na chamada
 *   do hook, antes de qualquer useEffect, evitando a race condition com o
 *   onIdTokenChanged do Firebase que chamaria signInAnonymously indevidamente.
 *
 * - Executa a verificação e login via custom token no mount.
 *   - OK          → signInWithCustomToken → Firebase user → /me
 *   - LOGINEMAIL   → sem verify: anônimo (a tela abre o login com Google)
 *   - EXPIRED_CODE → anônimo + comportamento configurável via `onExpiredCode`:
 *       · Se a tela passar `onExpiredCode` → chama o callback (tela decide o que fazer)
 *       · Se não passar (padrão) → fallback: redirect para /business/link/{id}
 *   - Erro        → fallback anônimo
 *
 * - "token" (formato UUID.AUTHCODE) NÃO é removido da URL após o auth,
 *   pois o componente (ex: BookingConfirmationScreen) ainda precisa do UUID.
 *
 * @param {object}   [options]
 * @param {Function} [options.onExpiredCode] - Callback chamado quando o código
 *   está expirado. Se omitido, o hook redireciona para /business/link/{id}
 *   (fallback padrão).
 * @param {Function} [options.verifyFn] - Função de verificação do auth code.
 *   Padrão: verifyAuthCode. Use verifyBookingAuthCode para o fluxo de booking confirmation.
 */
const useAuthCodeFlow = ({ onExpiredCode, verifyFn = verifyAuthCode } = {}) => {
	const dispatch = useDispatch();
	const history = useHistory();

	// ── Flag SÍNCRONA ─────────────────────────────────────────────────────────
	// Executado durante o render do componente pai (App), antes dos useEffects.
	const params = new URLSearchParams(window.location.search);
	const hasAuthParam = AUTH_CODE_PARAMS.some((p) => params.has(p));
	if (hasAuthParam && !window.__PRIT_CODE_LOGIN_IN_PROGRESS__) {
		window.__PRIT_CODE_LOGIN_IN_PROGRESS__ = true;
	}

	// ── Auth code flow (uma vez no mount) ─────────────────────────────────────
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);

		let authCode = null;
		let detectedParam = null;

		for (const param of AUTH_CODE_PARAMS) {
			const raw = params.get(param);
			const code = parseAuthCodeFromParam(param, raw);
			if (code) {
				authCode = code;
				detectedParam = param;
				break;
			}
		}

		if (!authCode) return;

		const run = async () => {
			let status = null;
			try {
				// Aguarda o Firebase restaurar o estado de auth do storage (IndexedDB)
				// antes de qualquer decisão — a restauração é assíncrona no mount.
				const resolvedUser = await new Promise((resolve) => {
					const unsubscribe = onAuthStateChanged(Firebase, (u) => {
						unsubscribe();
						resolve(u);
					});
				});

				// Se já há uma sessão válida (ex: reload pós-login WhatsApp), não precisa
				// chamar verifyAuthCode — apenas libera a flag e deixa a tela prosseguir.
				if (resolvedUser && !resolvedUser.isAnonymous) {
					return;
				}

				// Cliente sem telefone: não há código para verificar.
				if (authCode === EMAIL_LOGIN_AUTH_CODE) {
					if (!resolvedUser) await authService.authenticateAnonymousUser();
					return;
				}

				const result = await verifyFn(authCode);
				status = result?.data?.status;

				if (status === "EXPIRED_CODE") {
					await authService.authenticateAnonymousUser();
					if (onExpiredCode) {
						// A tela optou por tratar o código expirado por conta própria.
						onExpiredCode();
					} else {
						// Fallback padrão: redireciona para business/link preservando a rota atual.
						// Vai como goBackRoute (é o que a BusinessPage lê para voltar após o login e
						// para saber a origem do login no WhatsApp), sem o auth_code já expirado.
						const businessId = extractBusinessIdFromPath(window.location.pathname);
						if (businessId) {
							const returnParams = new URLSearchParams(params);
							if (detectedParam !== "token") returnParams.delete(detectedParam);
							const returnSearch = returnParams.toString() ? `?${returnParams.toString()}` : "";
							history.push(`/business/link/${businessId}`, {
								goBackRoute: window.location.pathname + returnSearch,
							});
						}
					}
					return;
				}

				if (status !== "OK" || !result?.data?.message) {
					await authService.authenticateAnonymousUser();
					return;
				}

				const userCredential = await signInWithCustomToken(Firebase, result.data.message);
				const firebaseUser = userCredential?.user;
				if (!firebaseUser) {
					await authService.authenticateAnonymousUser();
					return;
				}

				const idToken = await firebaseUser.getIdToken();
				dispatch(authAction.setUser(firebaseUser));
				dispatch(setMe(idToken));
				localStorage.setItem("@prit-web/accessToken", idToken);
				localStorage.setItem("@prit-web/accessTokenExpirationTime", String(Date.now() + 3600000));

				// Remove auth_code da URL. "token" é mantido pois carrega o UUID da transação.
				if (detectedParam !== "token") {
					const cleanParams = new URLSearchParams(params);
					cleanParams.delete(detectedParam);
					const cleanSearch = cleanParams.toString() ? `?${cleanParams.toString()}` : "";
					window.history.replaceState(null, "", window.location.pathname + cleanSearch);
				}
			} catch (err) {
				mixpanelTrackEvent("Error_Login_Automatico", {
					status,
					code: authCode,
					path: window.location.pathname,
					error_message: err?.message ?? "Erro desconhecido",
				});
				await authService.authenticateAnonymousUser();
			} finally {
				window.__PRIT_CODE_LOGIN_IN_PROGRESS__ = false;
			}
		};

		run();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useAuthCodeFlow;
